import { GateArtwork } from './GateArtwork';
import React, { useEffect, useId, useState } from 'react';
import type { GameState, Location, UserLanguage } from '../api';
import { artworkPath, displayName, evadeLabel, martialLawModifier } from './digitalMapGeometry';
import { DigitalMapGameplay, canShowMonsters, inspectionLocationActions, investigatorFor, locationActivities, revealedEncounter } from './digitalMapGame';
import { GameDialog } from './GameDialog';
import { getGateAbilityDescription, getGateDestinations, getGateImagePath, getGateTooltip } from './GateMarker';
import { InvestigatorMarker } from './InvestigatorMarker';
import { InvestigatorDisplay } from './InvestigatorDisplay';
import { MonsterDetailCard } from './Monsters';
import { PlayerHeader } from './PlayersList';
import { LocationActionIcon, LocationExtras } from './DigitalMapEntities';
import { MovementActionIcon, movementActionPresentation } from './MovementActionIcon';
import { SimpleMarkdown } from './SimpleMarkdown';
import { Encounter } from './wiki/Encounter';
import { useEncounterCatalog, useLocationEncounters } from './useLocationEncounters';
import { AncientWhisperInfo, ExhibitEncounterCatalog } from './AncientWhisperInfo';
import { LocationCardsSection, SpecialEncounterCards } from './LocationCards';
import { RuleText, buildRuleGroups } from './GameRules';
import { DigitalLocationRules } from './DigitalLocationRules';
import { OtherWorldColors } from './OtherWorldColors';
import { otherWorldInfo } from '../data/otherWorlds';
import { locationEncounterPresentation } from './locationEncounterPresentation';

function StabilityIndicator({ stable }: { stable: boolean }) {
  const id = useId();
  const [hover, setHover] = useState(false), [focused, setFocused] = useState(false), [pinned, setPinned] = useState(false);
  const title = stable ? 'Stable Location' : 'Unstable Location';
  const description = stable ? 'Gates cannot appear during Mythos phase. Gates cannot be sealed.' : 'Gates can appear during Mythos phase.';
  const open = hover || focused || pinned;
  return <span className="digital-location-stability" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
    <button type="button" aria-label={title} aria-describedby={open ? id : undefined} onFocus={() => setFocused(true)}
      onBlur={() => { setFocused(false); setPinned(false); }} onClick={() => setPinned(value => !value)}>
      <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true"><path d="M12 2 22 12 12 22 2 12Z" fill={stable ? '#48a96b' : '#cc4e46'} stroke="#142025" strokeWidth="2" /></svg>
    </button>
    {open && <span id={id} role="tooltip" className="digital-location-stability-tooltip"><strong>{title}</strong><br />{description}</span>}
  </span>;
}

function LocationArtwork({ name, world }: { name: string; world: boolean }) {
  const [failed, setFailed] = useState(false), [open, setOpen] = useState(false), [fullFailed, setFullFailed] = useState(false);
  if (failed) return null;
  const preview = artworkPath(name, world);
  return <div className="digital-location-artwork">
    <button type="button" aria-label={`View full artwork for ${displayName(name)}`} aria-haspopup="dialog"
      onClick={() => { setFullFailed(false); setOpen(true); }}>
      <img src={preview} alt={`${displayName(name)} artwork`} width={160} height={160} decoding="async"
        onError={() => { setFailed(true); setOpen(false); }} />
      <span className="digital-location-magnifier"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="10" cy="10" r="6" /><path d="m14.5 14.5 6 6" />
      </svg></span>
    </button>
    {open && <GameDialog title={`${displayName(name)} artwork`} variant="artwork" onClose={() => setOpen(false)}>
      {fullFailed ? <p role="alert">Artwork could not be loaded.</p> : <img className="digital-location-artwork-full"
        src={preview.replace('/rework/small/', '/rework/')} alt={`${displayName(name)} full artwork`} decoding="async"
        onError={() => setFullFailed(true)} />}
    </GameDialog>}
  </div>;
}

function EncounterCatalog({ name, world, language, context }: { name: string; world: boolean; language: UserLanguage; context: DigitalMapGameplay }) {
  const { groups, loading, error, retry } = useLocationEncounters(name, world, language, context.gameState);
  const count = groups.reduce((total, group) => total + group.texts.length, 0);
  if (loading || (!error && !count)) return null;
  return <section aria-label="Encounter reference" className="digital-location-section">
    <details className="digital-location-encounters">
      <summary>Encounters ({error ? 'unavailable' : count})</summary>
      <p className="digital-location-reference-note">Reference catalog for this game’s expansions. This is not the remaining deck or draw order; investigator abilities may change which encounters apply.</p>
      {error && <div role="alert"><p>{error}</p><button type="button" onClick={retry}>Retry encounters</button></div>}
      {!error && groups.map(group => <div key={group.name}>
        {groups.length > 1 && <h4>{displayName(group.name)}</h4>}
        {!group.texts.length && <p>No ordinary encounters for this location.</p>}
        {group.texts.map((text, index) => <div key={`${group.name}-${index}`} className="digital-location-encounter">
          <Encounter text={text} location={group.name} />
        </div>)}
      </div>)}
    </details>
  </section>;
}

function CultEncounterCatalog({ language, gameState }: { language: UserLanguage; gameState?: GameState }) {
  const { catalog, loading, error, retry } = useEncounterCatalog('CultEncounters', language, gameState);
  const encounters = catalog?.deckType === 'CultEncounters' ? catalog.cards : [];
  if (loading || (!error && !encounters.length)) return null;
  return <section aria-label="Cult encounter reference" className="digital-location-section">
    <details className="digital-location-encounters">
      <summary>Cult Encounters ({error ? 'unavailable' : encounters.length})</summary>
      <p className="digital-location-reference-note">Only investigators with the "One of the Thousand" cult membership resolve these encounters. This is a reference catalog, not the remaining deck or draw order.</p>
      {error && <div role="alert"><p>{error}</p><button type="button" onClick={retry}>Retry Cult encounters</button></div>}
      {!error && encounters.map((encounter, index) => <div key={`${encounter.id}-${index}`} className="digital-location-encounter">
        <h4>{encounter.name}</h4>
        <Encounter text={encounter.description} location="Cult Encounters" />
      </div>)}
    </details>
  </section>;
}

export function DigitalLocationDialog({ name, places, context, language, onClose, cityCount = 1, trainStations = [] }: {
  name: string; places: Location[]; context: DigitalMapGameplay; language: UserLanguage; onClose: () => void;
  cityCount?: number; trainStations?: { name: string; city: string }[];
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [selectedActivity, setSelectedActivity] = useState<string | null>(null);
  const activityKey = (card: ReturnType<typeof locationActivities>[number]) => card.id || JSON.stringify([card.name, card.type]);
  const activity = places.flatMap(place => locationActivities(place, context.gameState))
    .find(card => activityKey(card) === selectedActivity);
  useEffect(() => { setSelectedActivity(null); }, [name, context.gameState?.gameId]);
  useEffect(() => { if (!activity) setSelectedActivity(null); }, [activity]);
  const world = places.some(place => place.type === 'OtherWorld');
  const worldInfo = world ? otherWorldInfo(name) : undefined;
  const present = Array.from(new Set(places.flatMap(place => place.investigatorNames ?? [])));
  const first = places[0];
  const showCultEncounters = ['Black Cave', 'Unvisited Isle', 'Woods'].includes(name)
    && !!context.gameState?.gameSettings?.includeBlackGoatCards;
  const showTrainTravel = cityCount >= 2 && places.some(place => place.train);
  const encounterHints = first.type === 'Location' && !world
    ? [first.encounter1, first.encounter2].flatMap(value => {
      const hint = locationEncounterPresentation(value);
      return hint ? [hint] : [];
    }) : [];
  const district = !world && first.district && !['special', 'otherworld'].includes(first.district.toLowerCase())
    && displayName(first.district).toLowerCase() !== displayName(name).toLowerCase() ? displayName(first.district) : '';
  const title = `${displayName(name)}${district ? ` (${district})` : ''}`;
  const displayPlaces = world ? [{ ...first, name, investigatorNames: present, isClosed: places.some(place => place.isClosed),
    isSealed: places.some(place => place.isSealed), cluesNumber: places.reduce((total, place) => total + (place.cluesNumber || 0), 0),
    gates: places.flatMap(place => place.gates ?? []) }] : places;
  const specialTexts = Array.from(new Set(places.map(place => place.specialEncounterText?.trim()).filter((text): text is string => !!text)));
  const effectiveExpanded = expanded && present.includes(expanded) ? expanded : null;
  useEffect(() => { setExpanded(null); }, [name]);
  const submit = (id: string) => {
    const action = context.locationActions?.find(value => value.id === id);
    if (!context.onExecuteAction || context.loading || !action || action.disabled) return;
    onClose(); context.onExecuteAction(id);
  };
  return <GameDialog title={title} titleContent={<>
    {first.type === 'Location' && <StabilityIndicator stable={first.isStable} />}
    <span>{displayName(name)}{district && <span className="digital-location-neighborhood"> ({district})</span>}</span>
  </>} onClose={onClose} variant="location">
    {activity && <GameDialog title={activity.name || 'Activity'} onClose={() => setSelectedActivity(null)}>
      <RuleText detailed group={(context.gameState && buildRuleGroups(context.gameState).find(group => group.card && activityKey(group.card) === activityKey(activity)))
        || { key: activityKey(activity), name: activity.name, type: activity.type || 'Activity', card: activity, modifiers: [] }} />
    </GameDialog>}
    <div className="digital-location-dialog">
      <div className="digital-location-overview">
        {places.some(place => place.type !== 'Street') && !name.includes('Jail Cell') && <LocationArtwork key={name} name={name} world={world} />}
        {(!!worldInfo || !!encounterHints.length || !!specialTexts.length || showTrainTravel) && <div className="digital-location-overview-details">
          {worldInfo && <section className="digital-location-world-info" aria-label="Other World encounters">
            <h3>Encounter colors</h3>
            <OtherWorldColors name={name} labels />
            <p><strong>Danger level: {worldInfo.danger}</strong></p>
            <p>{worldInfo.description}</p>
            <p className="digital-location-reference-note">Color-based guide: green is easiest, followed by blue, yellow and red. Individual encounters and investigator abilities can change the danger.</p>
          </section>}
          {!!encounterHints.length && <section className="digital-location-encounter-hints" aria-label="Encounter hints">
            {encounterHints.map((hint, index) => <p key={index}>
              <img src={hint.icon} width={32} height={32} alt="" aria-hidden="true" />
              <span>{hint.description}</span>
            </p>)}
          </section>}
          {!!specialTexts.length && <section className="digital-location-special-section" aria-label="Special Encounter">
            <h3>Special Encounter</h3>{specialTexts.map(text => <p className="digital-location-special" key={text}>{text}</p>)}
            <SpecialEncounterCards location={first} />
          </section>}
          {showTrainTravel && <section className="digital-location-section" aria-label="Train travel">
            <h3>Train travel</h3>
            <p>During the Movement phase, you may spend $1 and 1 movement point to move to any other Train Station. This does not end the current investigator’s Movement phase; they may continue moving.</p>
            <h4>Train stations in this game</h4>
            <ul>{trainStations.map(station => <li key={station.name}>{displayName(station.name)} ({station.city})</li>)}</ul>
          </section>}
        </div>}
      </div>
      <DigitalLocationRules name={name} gameState={context.gameState} />
      {!world && places.some(place => (place.type === 'Location' || place.type === 'Street')
        && (place.isAquatic ?? place.aquatic ?? false)) && <section className="digital-location-section" aria-label="Aquatic location">
        <h3>Aquatic location</h3>
        <p>This location counts as adjacent to all other aquatic locations for aquatic monsters during movement.</p>
      </section>}
      {places.some(place => place.hasAncientWhisperMarker) && <AncientWhisperInfo />}
      {displayPlaces.map(place => {
        const actions = inspectionLocationActions(world ? places : [place], context);
        const ringIds = new Set(context.movementRing?.actions.map(action => action.id));
        const topEncounters = (world ? places : [place]).map(area => ({ place: area, card: revealedEncounter(area, context.gameState) }));
        const activities = locationActivities(place, context.gameState);
        const modifier = martialLawModifier(place.name, true);
        const statuses = [place.isClosed ? 'Closed' : '', place.isSealed ? 'Sealed' : ''].filter(Boolean);
        return <section key={place.name} aria-label="Location information">
          {!!statuses.length && <p className="digital-location-status">{statuses.join(' · ')}</p>}
          {modifier !== undefined && <section className="digital-location-section" aria-label="Martial Law">
            <h3>Martial Law</h3>
            <p>{context.gameState?.martialLawDeclared
              ? 'Martial Law was declared when the Ancient One’s doom track reached at least half full and remains in effect for the rest of the game.'
              : 'Martial Law will be declared when the Ancient One’s doom track is at least half full.'}</p>
            <p>While Martial Law is in effect, an investigator who ends their movement here must pass an {evadeLabel(modifier)} check or be arrested.</p>
          </section>}
          {place.hasPatrol && <section className="digital-location-section" aria-label="Patrol">
            <h3>Patrol</h3>
            <p>Each time an investigator attempts to leave this street or ends movement here, make a Sneak (+0) check. Failure: arrested. Remove all Patrol markers when the terror level rises.</p>
          </section>}
          {(world ? places : [place]).map(area => <LocationExtras key={area.name} location={area} context={context} inspect />)}
          {(world ? places : [place]).map(area => <LocationCardsSection key={`cards-${area.name}`} location={area} specialCardsInPlay={context.gameState?.specialCardsInPlay} />)}
          {!!activities.length && <section className="digital-location-section" aria-label="Activities"><h3>Activities</h3>
            {activities.map((source, i) => <article className="digital-location-activity" key={source.id || JSON.stringify([source.name, source.type])}>
              <h4>Source: <a href="#activity-details" aria-haspopup="dialog" onClick={event => {
                event.preventDefault(); setSelectedActivity(activityKey(source));
              }}>{source.name || 'Activity'}</a></h4>{source.type && <p>{source.type}</p>}
              {Array.from(new Set([source.textDescription?.trim(), source.rumorDescription?.trim()].filter((text): text is string => !!text)))
                .map((text, index) => <SimpleMarkdown key={index} text={text} keyPrefix={`activity-${i}-${index}`} />)}
            </article>)}
          </section>}
          {!!place.gates?.length && <section className="digital-location-section" aria-label="Gates"><h3>Gates</h3>
            {place.gates.map((gate, index) => <article className="digital-location-gate" key={gate.id ?? index}>
              <span className="digital-location-gate-artwork" title={getGateTooltip(gate)}><GateArtwork gate={gate} classicPath={getGateImagePath(gate, language)} alt={`${getGateDestinations(gate).join(' / ')} gate`} /></span>
              <div><h4>{getGateDestinations(gate).map(displayName).join(' / ')}</h4>
                <p>Modifier: {gate.modifier >= 0 ? '+' : ''}{gate.modifier}</p>
                <p>Dimension signs: {[gate.dimensionSign, gate.secondaryDimensionSign].filter(Boolean).join(' / ')}</p>
                {getGateAbilityDescription(gate) && <p className="digital-location-gate-traits">{getGateAbilityDescription(gate)}</p>}
              </div>
            </article>)}
          </section>}
          {canShowMonsters(place) && !!place.monsters?.length && <section className="digital-location-section" aria-label="Monsters"><h3>Monsters</h3>
            <div className="digital-location-monsters">{place.monsters.map((monster, index) => <div className="digital-location-monster" tabIndex={0} aria-label={`${monster.name} details`} key={index}><MonsterDetailCard monster={monster} /></div>)}</div>
          </section>}
          {!!place.investigatorNames?.length && <section className="digital-location-section" aria-label="Investigators"><h3>Investigators</h3>
            <div className="digital-location-investigators">{place.investigatorNames.map(investigatorName => <button type="button" key={investigatorName}
              aria-label={`Inspect ${investigatorName}`} aria-expanded={effectiveExpanded === investigatorName}
              onClick={() => setExpanded(value => value === investigatorName ? null : investigatorName)}>
              <InvestigatorMarker investigator={investigatorFor(investigatorName, context) ?? { name: investigatorName, delayed: false }} offsetByIndex={false} size={56} />
              <span>{investigatorName}</span>
            </button>)}</div>
            {effectiveExpanded && place.investigatorNames.includes(effectiveExpanded) && (() => {
              const investigator = investigatorFor(effectiveExpanded, context);
              if (!investigator) return <p>Investigator details are unavailable.</p>;
              const actions = (context.locationActions ?? []).filter(action => action.investigatorName?.toLowerCase() === effectiveExpanded.toLowerCase())
                .map(action => ({ ...action, disabled: !!context.loading || !!action.disabled }));
              return <div className="digital-location-investigator-sheet">
                {context.gameState && <PlayerHeader gameState={context.gameState} investigator={investigator} />}
                <section className="game-investigator-sheet-scroll" tabIndex={0} aria-label={`${effectiveExpanded} investigator sheet`}>
                  <InvestigatorDisplay investigator={investigator} gameState={context.gameState} currentLocationName={places.find(area => area.investigatorNames?.includes(effectiveExpanded))?.name ?? place.name} showStats
                    language={language} availableActions={actions} onExecuteAction={context.onExecuteAction ? submit : undefined}
                    deckInfo={{ commonItems: context.gameState?.decks?.commonItems, uniqueItems: context.gameState?.decks?.uniqueItems, spells: context.gameState?.decks?.spells }} />
                </section>
              </div>;
            })()}
          </section>}
          {!!actions.length && <section className="digital-location-section" aria-label="Actions"><h3>Actions</h3>
            <div className="digital-location-actions">{actions.map(action => <button type="button" key={action.id}
              className={ringIds.has(action.id) ? 'movement-action' : undefined}
              style={ringIds.has(action.id) ? movementActionPresentation(action).style : undefined}
              disabled={!!context.loading || !!action.disabled || !context.onExecuteAction} title={action.description} onClick={() => submit(action.id)}>
              {ringIds.has(action.id) ? <MovementActionIcon action={action} /> : <LocationActionIcon action={action} />}<span>{action.name}</span>
            </button>)}</div>
          </section>}
          {topEncounters.filter(entry => !!entry.card?.name).map(entry => <section key={entry.place.name} className="digital-location-section" aria-label="Revealed encounter"><h3>Revealed encounter</h3><Encounter text={entry.card!.name} location={entry.place.name} /></section>)}
        </section>;
      })}
      <EncounterCatalog name={name} world={world} language={language} context={context} />
      {showCultEncounters && <CultEncounterCatalog language={language} gameState={context.gameState} />}
      {places.some(place => place.hasAncientWhisperMarker) && <ExhibitEncounterCatalog language={language} gameState={context.gameState} />}
    </div>
  </GameDialog>;
}
